const ZERO_DECIMAL_CURRENCIES = new Set(['KRW', 'JPY', 'VND', 'IDR']);

const RATE_FORMAT_WHOLE = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// Sub-1 rates (KRW ≈ 0.0587, JPY ≈ 0.62 RUB per unit) keep 4 significant digits,
// so KRW reads "0,05873" instead of collapsing to "0,0587" / "0,06".
const RATE_FORMAT_FRACTION = new Intl.NumberFormat('ru-RU', {
  minimumSignificantDigits: 4,
  maximumSignificantDigits: 4,
});

/** Parses a rate as sent by the API; NaN for anything that is not a plain number. */
export function parseRate(rate: string | number): number {
  if (typeof rate === 'number') return rate;
  return rate.trim() === '' ? NaN : Number(rate);
}

/** True for a rate the UI can show: finite and > 0. Zero, negatives and garbage count as missing. */
export function isUsableRate(rate: string | number): boolean {
  const n = parseRate(rate);
  return Number.isFinite(n) && n > 0;
}

/**
 * Formats a currency-to-RUB rate for the FX ticker / currency picker: "92,45" for
 * rates >= 1, "0,05873" (4 significant digits) below 1, "—" for unusable input.
 * Intl rounds half away from zero.
 */
export function formatRate(rate: string | number): string {
  if (!isUsableRate(rate)) return '—';
  const n = parseRate(rate);
  return (n >= 1 ? RATE_FORMAT_WHOLE : RATE_FORMAT_FRACTION).format(n);
}

const CURRENCY_CODE = /^[A-Z]{3}$/;

// Raw currency codes already reported, so a bad value shown in many rows warns once per page session.
const warnedCurrencyCodes = new Set<string>();

export function formatMoney(amount: string, currency: string, locale: string): string {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return '—';
  const code = currency.trim().toUpperCase();
  if (!CURRENCY_CODE.test(code)) {
    if (!warnedCurrencyCodes.has(currency)) {
      warnedCurrencyCodes.add(currency);
      console.warn('[formatMoney] unusable currency code', {
        code: JSON.stringify(currency),
        amount,
        locale,
      });
    }
    return '—';
  }
  const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(n);
}
